import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type HistoryMode = 'push' | 'replace';

/**
 * Low-level atomic URL-param writer shared by all the field hooks.
 *
 * Every mutation goes through a single functional `setSearchParams` update, so
 * a caller can change several params (e.g. set a filter AND drop `page`) in ONE
 * history entry / ONE re-render. This is what prevents the two-write →
 * double-fetch race: the field hooks reset pagination in the same patch call.
 *
 *   patch((p) => { p.set('status', 'X'); p.delete('page'); })  // one write
 *
 * `history` defaults to 'replace' (no history spam from filters / search /
 * pagination). Discrete navigations like a tab switch pass 'push' so the
 * browser Back button steps through them.
 */
export function useUrlPatch() {
  const [searchParams, setSearchParams] = useSearchParams();

  const patch = useCallback(
    (mutate: (params: URLSearchParams) => void, history: HistoryMode = 'replace') => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: history !== 'push' }
      );
    },
    [setSearchParams]
  );

  return { searchParams, patch };
}
